import { useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";
import localForage from "localforage";
import { FREYA_DEVICE_ID_KEY } from "./FreyaPrintSettings";
import { Nullable } from "../../../common/types";

interface QrScannerRef {
  scanner: Nullable<QrScanner>;
}

export const useInitCameraDevices = (useIndexDB = false) => {
  const qrScanner = useRef<QrScannerRef>({ scanner: null });
  const hasRequestedCameraAccess = useRef(false);
  const latestStorageKey = useRef<string | undefined>();
  const [selectedCameraDevice, setSelectedCameraDevice] = useState<QrScanner.Camera>();
  const [cameraDevices, setCameraDevices] = useState<Array<QrScanner.Camera>>([]);
  const [deviceIdFormIndexDB, setDeviceIdFromIndexDB] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const requestVideoPermission = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      return null;
    }

    return navigator.mediaDevices.getUserMedia({ video: true });
  };

  const getDevices = async () => {
    const devices = await QrScanner.listCameras();

    if (devices.length > 0 && hasRequestedCameraAccess.current) {
      return devices;
    }

    hasRequestedCameraAccess.current = true;
    const permissionStream = await requestVideoPermission().catch(() => null);
    permissionStream?.getTracks().forEach(track => {
      track.stop();
    });

    const syncedDevices = await QrScanner.listCameras();
    return syncedDevices;
  };

  const setCamera = async (camera: QrScanner.Camera) => {
    if (!camera) {
      return;
    }
    setSelectedCameraDevice(camera);
  };

  const initCameraDevices = async (key?: string) => {
    latestStorageKey.current = key;
    const devices = await getDevices();
    setCameraDevices(devices);
    const deviceId = (await localForage.getItem(key || FREYA_DEVICE_ID_KEY)) as Nullable<string>;
    const hasSavedDevice = deviceId !== null && deviceId !== undefined;
    const device = useIndexDB && hasSavedDevice ? devices.find(device => device.id === deviceId) : devices[0];

    setDeviceIdFromIndexDB((deviceId || "") as string);
    await setCamera(device!);
    return device;
  };

  const initDevices = async (key?: string) => {
    try {
      await initCameraDevices(key);
      setError(false);
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!navigator?.mediaDevices?.addEventListener) {
      return;
    }

    const handleDeviceChange = () => {
      initDevices(latestStorageKey.current);
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, []);

  return {
    cameraDevices,
    selectedCameraDevice,
    setSelectedCameraDevice,
    qrScanner,
    loading,
    error,
    setCamera,
    initCameraDevices,
    initDevices,
    deviceIdFormIndexDB
  };
};
